import {
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isMarqueeZoomPointerStart } from '@/lib/zoom/editor-canvas-zoom-pointer'
import { MARQUEE_MIN_SIZE_PX } from '../constants/marquee'
import { computePointerDragRect } from '../helpers/drag-rect'
import type { MarqueeZoomDragState } from '../types/canvas-drag-state'

/**
 * Zoom marquee: the sticky Z mode, the "Z held" flag, and the drag that zooms to the rect it
 * spans. `applyZoomToCanvasRectRef` is filled in by `use-viewport-zoom-commands`.
 */
export function useMarqueeZoom({
  internalShapeManagerRef,
  scaleRef,
  getCanvasPointFromPointerEvent,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  scaleRef: MutableRefObject<number>
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
}) {
  /** Toggle with Z: next drag on canvas zooms to marquee rect (canvas coords). */
  const [isMarqueeZoomMode, setIsMarqueeZoomMode] = useState(false)
  /** While Z is physically held (in addition to sticky mode), marquee zoom is available. */
  const zMarqueeKeyHeldRef = useRef(false)
  const [zMarqueeKeyHeld, setZMarqueeKeyHeld] = useState(false)
  const [marqueeZoom, setMarqueeZoom] = useState<MarqueeZoomDragState | null>(null)
  const applyZoomToCanvasRectRef = useRef<
    ((b: { x: number; y: number; width: number; height: number }) => void) | null
  >(null)

  /** Ctrl+Shift (or Cmd+Shift), sticky Z mode, or Z key held: marquee zoom steals pointer in capture before shapes. */
  const handleMarqueeZoomPointerDownCapture = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return

      const marqueeZoomChord = isMarqueeZoomPointerStart(e)
      const marqueeZoomActive = marqueeZoomChord || isMarqueeZoomMode || zMarqueeKeyHeldRef.current

      if (!marqueeZoomActive) return

      const mgr = internalShapeManagerRef.current

      if (mgr?.isInteracting()) return

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return

      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      setMarqueeZoom({
        pointerId: e.pointerId,
        startCanvasX: pt.canvasX,
        startCanvasY: pt.canvasY,
        currentCanvasX: pt.canvasX,
        currentCanvasY: pt.canvasY,
      })
    },
    [getCanvasPointFromPointerEvent, isMarqueeZoomMode, internalShapeManagerRef]
  )

  const handleMarqueeZoomPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (!marqueeZoom || e.pointerId !== marqueeZoom.pointerId) return false

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return true
      e.preventDefault()
      setMarqueeZoom((prev) =>
        prev ? { ...prev, currentCanvasX: pt.canvasX, currentCanvasY: pt.canvasY } : prev
      )
      return true
    },
    [marqueeZoom, getCanvasPointFromPointerEvent]
  )

  const handleMarqueeZoomPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (!marqueeZoom || e.pointerId !== marqueeZoom.pointerId) return false

      e.preventDefault()

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }

      const zoomRect = computePointerDragRect(marqueeZoom, scaleRef.current)

      if (zoomRect) {
        if (zoomRect.width >= MARQUEE_MIN_SIZE_PX && zoomRect.height >= MARQUEE_MIN_SIZE_PX) {
          applyZoomToCanvasRectRef.current?.(zoomRect)
          setIsMarqueeZoomMode(false)
        }
      }

      setMarqueeZoom(null)
      return true
    },
    [marqueeZoom, scaleRef]
  )

  return {
    isMarqueeZoomMode,
    setIsMarqueeZoomMode,
    zMarqueeKeyHeldRef,
    zMarqueeKeyHeld,
    setZMarqueeKeyHeld,
    marqueeZoom,
    setMarqueeZoom,
    applyZoomToCanvasRectRef,
    handleMarqueeZoomPointerDownCapture,
    handleMarqueeZoomPointerMove,
    handleMarqueeZoomPointerUp,
  }
}
